/**
 * The parts of a state notifications compare from one tick to the next (the
 * sim mutates its state in place, so each tick is summarised first).
 */
import { getHexTileMapKey } from '../../hexmap/model/keys';
import type { RuleSet } from '../../rules';
import {
  bonusCity,
  buildingLevel,
  buildingMaxHp,
  type Cell,
  cityBonuses,
  type EntityId,
  engagements,
  fromMilli,
  isCity,
  type PlayerId,
  type Plunder,
  type Raze,
  type SimContext,
  type SimState,
  territoryOwners,
  unitAmounts,
} from '../../sim';

/** One unit or building as notifications compare it. */
export interface EntitySummary {
  type: string;
  owner: PlayerId;
  pos: Cell;
  /** Whole hp (the sim keeps thousandths). */
  hp: number;
  moving?: boolean;
  auto?: boolean;
  /** A unit: inside a building. */
  inside?: boolean;
  /**
   * A unit walking out of a building because a promotion made it a type the
   * building doesn't take (cavalry in a barracks becoming a tank), not on an
   * order: its arrival isn't reported (the promotion toast already was).
   * Needs the `prev` summary; kept while it walks to the same tile.
   */
  stepOut?: boolean;
  /** A moving unit's path end ("q,r"), to tell a step out from a later order. */
  pathEnd?: string;
  /** A city's stock (resources live in cities). */
  stock?: Record<string, number>;
  /** An own city's max hp (with `rules`), for "City 2 under attack · 900/1,650 hp". */
  maxHp?: number;
  /** Completed (buildings only). */
  done?: boolean;
  /** An own building's level (1 and up), to spot a rise (`levelEvents`). */
  level?: number;
  /** A barracks: the own city whose territory it stands in. */
  cityId?: EntityId;
  /** A barracks whose city has fallen: the city it now serves, if another. */
  nowCityId?: EntityId;
  /** An own unit: the own completed city whose territory it stands in (or is inside). */
  homeTerritory?: EntityId;
  /** A spy: the rival city it's disguised in. */
  disguiseCityId?: EntityId;
  /** A spy its victim exposed. */
  exposed?: boolean;
  /** An own spy: the rival whose city's territory it stands in (under its feet, so in sight). */
  rivalTerritory?: PlayerId;
  /** An exposed rival unit: the viewer's completed city whose territory it stands in. */
  caughtIn?: EntityId;
  /**
   * An own unit's group: the live one, else (with `prev`) the one it was
   * last in while that group is gone, its number not taken again and it is
   * the only one left carrying it, so the group's last survivor still counts
   * as its member ("Group 2 wiped out"); units the player ungrouped don't.
   */
  group?: { id: EntityId; number: number };
}

export interface StateSummary {
  units: Map<EntityId, EntitySummary>;
  buildings: Map<EntityId, EntitySummary>;
  /** Each player's latest plunder (see PlayerState.lastPlunder). */
  plunders: Map<PlayerId, Plunder>;
  /** Each player's latest razing (see PlayerState.lastRaze). */
  razes: Map<PlayerId, Raze>;
  /** The player's units in training (`Building.training`) by id: the city and unit type. */
  training: Map<EntityId, { cityId: EntityId; type: string }>;
}

/**
 * The parts of the state notifications compare (the sim mutates state in
 * place). With `rules`, a barracks (a building with `capacity`) owned by
 * `playerId` records the city it serves, so its loss can name it (only own
 * losses are reported, so rivals' barracks skip the territory lookup). Pass
 * the `prev` summary so a barracks whose city has fallen keeps that city: its
 * land may pass to another of the player's cities before the barracks itself
 * falls, and its loss should still name the city it was built for.
 */
export function summarize(
  state: SimState,
  rules?: RuleSet,
  playerId?: PlayerId,
  prev?: StateSummary,
  real: SimState = state
): StateSummary {
  const units = new Map<EntityId, EntitySummary>();
  let owners: ReturnType<typeof territoryOwners> | undefined;
  // The numbers of the player's live groups, so a gone group's number taken
  // by a new one stops being carried.
  const groupNumbers = new Set<number>();
  for (const u of Object.values(state.units)) {
    const g =
      u.owner === playerId && u.groupId !== undefined ? state.groups?.[u.groupId] : undefined;
    if (g) groupNumbers.add(g.number);
  }
  // How many living units had each group last summary: a gone group with one
  // left is its last survivor's (dissolved by losses); several left means it
  // was ungrouped, and nobody carries it.
  const carriers = new Map<EntityId, number>();
  if (prev) {
    for (const u of Object.values(state.units)) {
      const g = prev.units.get(u.id)?.group;
      if (g) carriers.set(g.id, (carriers.get(g.id) ?? 0) + 1);
    }
  }
  for (const u of Object.values(state.units)) {
    // Own units: which own city's territory they're in, so a fallen city can
    // count the units that died with it. Only units really theirs (`real`):
    // a rival spy shown as one of their workers (viewerState.ts) vanishing
    // with the city isn't a loss.
    let homeTerritory: EntityId | undefined;
    let rivalTerritory: PlayerId | undefined;
    if (rules && u.owner === playerId && real.units[u.id]?.owner === playerId) {
      owners ??= territoryOwners(state, rules);
      const city = state.buildings[owners.get(getHexTileMapKey(u.pos)) ?? ''];
      if (city?.owner === playerId) homeTerritory = city.id;
      else if (city && rules.units[u.type]?.stealth) rivalTerritory = city.owner;
    }
    // An exposed rival spy in the viewer's territory (their own land is in
    // their sight), for "Spy caught in City 1".
    let caughtIn: EntityId | undefined;
    if (rules && playerId !== undefined && u.owner !== playerId && u.exposed) {
      owners ??= territoryOwners(state, rules);
      const city = state.buildings[owners.get(getHexTileMapKey(u.pos)) ?? ''];
      if (city?.owner === playerId) caughtIn = city.id;
    }
    const moving = u.path !== undefined && u.path.length > 0;
    const pathEnd = moving && u.path ? getHexTileMapKey(u.path[u.path.length - 1]) : undefined;
    const was = prev?.units.get(u.id);
    let group: EntitySummary['group'];
    if (u.owner === playerId) {
      const live = u.groupId !== undefined ? state.groups?.[u.groupId] : undefined;
      if (live && u.groupId !== undefined) group = { id: u.groupId, number: live.number };
      else if (
        was?.group &&
        !state.groups?.[was.group.id] &&
        !groupNumbers.has(was.group.number) &&
        carriers.get(was.group.id) === 1
      ) {
        group = was.group;
      }
    }
    const stepOut =
      moving &&
      !u.inside &&
      was !== undefined &&
      ((was.inside === true && was.type !== u.type) ||
        (was.stepOut === true && was.pathEnd === pathEnd));
    units.set(u.id, {
      type: u.type,
      owner: u.owner,
      pos: { ...u.pos },
      hp: fromMilli(u.hp),
      moving,
      auto: u.auto !== undefined,
      ...(u.inside ? { inside: true } : {}),
      ...(stepOut ? { stepOut: true } : {}),
      ...(pathEnd ? { pathEnd } : {}),
      ...(homeTerritory ? { homeTerritory } : {}),
      ...(u.disguise ? { disguiseCityId: u.disguise.cityId } : {}),
      ...(u.exposed ? { exposed: true } : {}),
      ...(rivalTerritory ? { rivalTerritory } : {}),
      ...(caughtIn ? { caughtIn } : {}),
      ...(group ? { group } : {}),
    });
  }
  const buildings = new Map<EntityId, EntitySummary>();
  const training = new Map<EntityId, { cityId: EntityId; type: string }>();
  let bonuses: ReturnType<typeof cityBonuses> | undefined;
  for (const b of Object.values(state.buildings)) {
    let maxHp: number | undefined;
    if (rules && b.owner === playerId && isCity(b, rules)) {
      bonuses ??= cityBonuses(state, rules);
      maxHp = fromMilli(buildingMaxHp(state, rules, b, bonuses));
    }
    let cityId: EntityId | undefined;
    let nowCityId: EntityId | undefined;
    if (
      b.owner === playerId &&
      rules?.buildings[b.type]?.capacity !== undefined &&
      b.buildProgress >= 1
    ) {
      const served = prev?.buildings.get(b.id)?.cityId;
      owners ??= territoryOwners(state, rules);
      const now = bonusCity(state, rules, b, owners)?.id;
      if (served !== undefined && !state.buildings[served]) {
        cityId = served;
        if (now !== undefined && now !== served) nowCityId = now;
      } else {
        cityId = now;
      }
    }
    if (b.owner === playerId) {
      for (const t of b.training ?? []) training.set(t.id, { cityId: b.id, type: t.unitType });
    }
    buildings.set(b.id, {
      type: b.type,
      owner: b.owner,
      pos: { ...b.pos },
      hp: fromMilli(b.hp),
      done: b.buildProgress >= 1,
      ...(b.owner === playerId ? { level: buildingLevel(b) } : {}),
      ...(b.stock ? { stock: unitAmounts(b.stock) } : {}),
      ...(maxHp !== undefined ? { maxHp } : {}),
      ...(cityId ? { cityId } : {}),
      ...(nowCityId ? { nowCityId } : {}),
    });
  }
  const plunders = new Map<PlayerId, Plunder>();
  const razes = new Map<PlayerId, Raze>();
  for (const [id, p] of Object.entries(state.players)) {
    // In whole units, like every amount the feed shows.
    if (p.lastPlunder) {
      plunders.set(id, { ...p.lastPlunder, amounts: unitAmounts(p.lastPlunder.amounts) });
    }
    if (p.lastRaze) razes.set(id, { ...p.lastRaze, amounts: unitAmounts(p.lastRaze.amounts) });
  }
  return { units, buildings, plunders, razes, training };
}

/**
 * The player's units and buildings some rival is hitting this tick (sim
 * `engagements`, or this tick's `engaged` when the caller has them). Fog-safe:
 * it only says their own things are being hit.
 */
export function engagedOwn(
  state: SimState,
  ctx: SimContext,
  playerId: PlayerId,
  engaged: ReadonlyMap<EntityId, EntityId> = engagements(state, ctx)
): Set<EntityId> {
  const hit = new Set<EntityId>();
  for (const [attackerId, targetId] of engaged) {
    const attacker = state.units[attackerId] ?? state.buildings[attackerId];
    const target = state.units[targetId] ?? state.buildings[targetId];
    if (attacker && target && attacker.owner !== playerId && target.owner === playerId) {
      hit.add(targetId);
    }
  }
  return hit;
}
